import { type Lap } from './Lap'
import { type GpsPoint } from '../telemetry/processed/GpsPoint'
import {
  calculateMovingAverageSpeed,
  filterTroughsByLowestSpeedWithinWindow,
  findPeaks,
  identifyTroughsBetweenPeaks,
} from '../../utils/telemetryUtils'

/**
 * Lap analysis utilities for computing breaking points, slowest points, and other
 * derived metrics from lap telemetry data.
 *
 * These are pure functions that take a Lap and return computed results.
 * Use useMemo in components to cache results.
 */

export interface BreakingAndSlowestPoints {
  breakingPoints: GpsPoint[]
  slowestPoints: GpsPoint[]
  topSpeed: number
}

/**
 * Finds breaking points (speed peaks followed by troughs) and slowest points in a lap.
 *
 * Breaking points are locations where the driver significantly reduced speed from a peak,
 * typically indicating corners or braking zones. The algorithm:
 * 1. Finds speed peaks (local maximums)
 * 2. Identifies troughs (local minimums) between peaks
 * 3. Filters peaks where speed drop exceeds threshold
 *
 * Slowest points are the troughs filtered by lowest speed within a time window.
 *
 * @param lap - The lap to analyze
 * @param options - Configuration options
 * @returns Object containing breaking points, slowest points, and top speed
 *
 * @example
 * ```tsx
 * function LapAnalysis({ lap }: { lap: Lap }) {
 *   const { breakingPoints, slowestPoints, topSpeed } = useMemo(
 *     () => computeBreakingPoints(lap),
 *     [lap]
 *   )
 *
 *   return <Map breakingPoints={breakingPoints} slowestPoints={slowestPoints} />
 * }
 * ```
 */
export function computeBreakingPoints(
  lap: Lap,
  options: {
    windowSize?: number
    thresholdPercentage?: number
  } = {}
): BreakingAndSlowestPoints {
  const { windowSize = 3, thresholdPercentage = 15 } = options

  const gpsPoints = lap.processedTelemetryData.gpsPoints as GpsPoint[]

  if (gpsPoints.length === 0) {
    return { breakingPoints: [], slowestPoints: [], topSpeed: 0 }
  }

  // Calculate moving average speeds
  const smoothedSpeeds = calculateMovingAverageSpeed(gpsPoints, windowSize)
  const topSpeed = Math.max(...smoothedSpeeds)
  const speedThreshold = topSpeed * (thresholdPercentage / 100)

  // Identify all potential peaks and troughs in the smoothed speed data
  const peaks: number[] = findPeaks(smoothedSpeeds)
  let troughs: number[] = identifyTroughsBetweenPeaks(peaks, smoothedSpeeds)

  // Check if lap ends with deceleration
  let isDeceleratingToEnd = true
  const lastComparisonPoint = Math.max(troughs.at(-1) ?? 0, peaks.at(-1) ?? 0)
  for (let i = lastComparisonPoint + 1; i < smoothedSpeeds.length - 1; i++) {
    // If any subsequent speed is greater than the previous, it's not decelerating to the end
    if (smoothedSpeeds[lastComparisonPoint] < smoothedSpeeds[i]) {
      isDeceleratingToEnd = false
      break
    }
  }

  // If the lap ends with deceleration, consider the last data point as a trough
  if (isDeceleratingToEnd) {
    troughs.push(smoothedSpeeds.length - 1)
  }

  // Filter peaks by speed drop threshold to get breaking points
  const breakingPoints = peaks
    .filter((peak: number, index: number) => {
      const nextTroughIndex = troughs[index]
      return (
        smoothedSpeeds[peak] - smoothedSpeeds[nextTroughIndex] >= speedThreshold
      )
    })
    .map((peakIndex: number) => gpsPoints[peakIndex])

  // Remove the last trough if it was added for end deceleration
  if (isDeceleratingToEnd) {
    troughs = troughs.slice(0, troughs.length - 1)
  }

  // Filter troughs by lowest speed within time window to get slowest points
  const filteredTroughIndices = filterTroughsByLowestSpeedWithinWindow(
    gpsPoints,
    troughs,
    smoothedSpeeds
  )
  const slowestPoints = filteredTroughIndices.map(
    (troughIndex: number) => gpsPoints[troughIndex]
  )

  return {
    breakingPoints,
    slowestPoints,
    topSpeed,
  }
}

/**
 * Compute and populate breaking/slowest points directly on a Lap instance.
 * This mutates the lap object by setting breakingPoints, slowestPoints, and topSpeed.
 *
 * Use this when you want to compute analysis once and store it on the lap.
 * For reactive/memoized patterns, use computeBreakingPoints() instead.
 *
 * @param lap - The lap to analyze and populate
 * @param options - Configuration options
 *
 * @example
 * ```typescript
 * const lap = new Lap(metadata, bounds, telemetry)
 * populateLapAnalysis(lap)
 * // Now lap.breakingPoints, lap.slowestPoints, and lap.topSpeed are set
 * ```
 */
export function populateLapAnalysis(
  lap: Lap,
  options?: Parameters<typeof computeBreakingPoints>[1]
): void {
  const result = computeBreakingPoints(lap, options)
  lap.breakingPoints = result.breakingPoints
  lap.slowestPoints = result.slowestPoints
  lap.topSpeed = result.topSpeed
}
